import React, { FunctionComponent } from 'react';
import Linkify from 'react-linkify';
import { connect } from 'react-redux';

import styled from 'styled-components';

// Store
import AppState from '@webapp/store/state';
import { getFullName } from '@webapp/store/user/selectors';

import { Translate } from 'react-localize-redux';

// Types
import { SyncBrief } from '@services/sync-license/types/sync-brief';

// Components
import NewMenu from '@components/new-menu';
import Typography from '@components/basics/typography';

// Controllers
import UserAvatarInfo from '@components/basics/user-avatar-info';
import DefaultAvatar from '@components/basics/default-avatar';

interface SyncNotificationMenuProps {
  className?: string;
  syncBrief?: SyncBrief;
  fullName?: string;
  avatar?: string;
  noButton?: boolean;
  isMobile: boolean;
}

const mapStateToProps = (state: AppState) => ({
  avatar: state.user.profile?.avatar,
  fullName: getFullName(state),
  syncBrief: state.notifications.popupNotificationPayload as SyncBrief,
  isMobile: state.ui.mobile,
});

const SyncNotificationMenu: FunctionComponent<SyncNotificationMenuProps> = ({
  className,
  noButton,
  avatar,
  fullName,
  syncBrief,
  isMobile,
}) => {
  return (
    <NewMenu
      className={className}
      noButton={noButton}
      menuId="SYNC_NOTIFICATION_MENU"
      centeredPadding={!isMobile}
      topHeader={isMobile && <UserAvatarInfo src={avatar} name={fullName!} />}
      header={!isMobile && <UserAvatarInfo src={avatar} name={fullName!} />}
    >
      <StyledDefaultAvatar />
      <Typography
        className="mb-40"
        type="analytics-nav"
        customFont="semi-bold"
        align={'center'}
      >
        <Translate id="SYNC_NOTIFICATION.MENU.TITLE" />
      </Typography>
      <Typography className="mb-40" align={'left'}>
        <Translate id="SYNC_NOTIFICATION.MENU.SUBTITLE" />
      </Typography>

      <MenuTypography align={'left'}>
        <b>
          <Translate id="SYNC_NOTIFICATION.IMPORTANT.TITLE" />
        </b>
        <ImportantList>
          <li>
            <Translate id="SYNC_NOTIFICATION.IMPORTANT.TEXT1" />
          </li>
          <li>
            <Translate id="SYNC_NOTIFICATION.IMPORTANT.TEXT2" />
          </li>
          <li>
            <Translate id="SYNC_NOTIFICATION.IMPORTANT.TEXT3" />
          </li>
          <li>
            <Translate id="SYNC_NOTIFICATION.IMPORTANT.TEXT4" />
          </li>
        </ImportantList>
      </MenuTypography>

      <MenuTypography align={'left'}>
        <b>
          <Translate id="SYNC_NOTIFICATION.MENU.SUBJECT.TITLE" />
        </b>
        {syncBrief?.subject}
      </MenuTypography>
      <MenuTypography align={'left'}>
        <b>
          <Translate id="SYNC_NOTIFICATION.MENU.EARNINGS.TITLE" />
        </b>
        {syncBrief?.earnings_potential}
      </MenuTypography>
      <MenuTypography align={'left'}>
        <b>
          <Translate id="SYNC_NOTIFICATION.MENU.DEADLINE.TITLE" />
        </b>
        {syncBrief?.submission_deadline
          ? new Date(syncBrief?.submission_deadline).toDateString()
          : ''}
      </MenuTypography>
      <MenuTypography align={'left'}>
        <b>
          <Translate id="SYNC_NOTIFICATION.MENU.DETAILS.TITLE" />
        </b>
        {syncBrief?.description}
      </MenuTypography>
      <MenuTypography align={'left'}>
        <b>
          <Translate id="SYNC_NOTIFICATION.MENU.APPLY.TITLE" />
        </b>
        <Linkify
          componentDecorator={(decoratedHref, decoratedText, key) => (
            <a
              target="blank"
              style={{ fontWeight: 'normal', color: 'blue' }}
              href={decoratedHref}
              key={key}
            >
              {decoratedText}
            </a>
          )}
        >
          {syncBrief?.how_to_apply}
        </Linkify>
      </MenuTypography>
    </NewMenu>
  );
};

export default connect(mapStateToProps)(SyncNotificationMenu);
const StyledDefaultAvatar = styled(DefaultAvatar)`
  width: 116px;
  height: 116px;
  border-radius: 50%;
  overflow: hidden;
  display: block;
  margin: 0 auto 32px;
`;

const MenuTypography = styled(Typography)`
  margin-bottom: 20px;
  font-size: ${({ theme }) => theme.typography.h4};
`;

const ImportantList = styled.ul`
  margin: 0px;
  padding: 0px 20px;
`;
